import React from 'react';

import formatString from 'format-string-by-pattern';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Block, FormField } from 'components';
import { DEFAULT_PHONE_FIELD_MASK } from 'coupon-common';
import {
  composeSyncValidators,
  maxLength,
  required,
  validEmail,
  validPassword,
  validPhone,
} from 'coupon-common/src/utils';

import messages from './messages';

const phoneFieldMask = formatString(DEFAULT_PHONE_FIELD_MASK);

const SOCIAL_FIELDS = [
  {
    key: 'instagram',
    label: 'Instagram',
    placeholder: '@username or full URL',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    placeholder: '@username or full URL',
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    placeholder: '@username or full URL',
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    placeholder: '@username or full URL',
  },
  {
    key: 'youtube',
    label: 'YouTube',
    placeholder: '@channel or full URL',
  },
];

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const getSocialUrl = (platform, value) => {
  if (!value) {
    return '';
  }

  const trimmedValue = value.trim();

  if (
    trimmedValue.startsWith('http://') ||
    trimmedValue.startsWith('https://')
  ) {
    return trimmedValue;
  }

  const username = trimmedValue.replace(/^@/, '');

  switch (platform) {
    case 'instagram':
      return `https://www.instagram.com/${username}`;

    case 'facebook':
      return `https://www.facebook.com/${username}`;

    case 'linkedin':
      return `https://www.linkedin.com/company/${username}`;

    case 'tiktok':
      return `https://www.tiktok.com/@${username}`;

    case 'youtube':
      return `https://www.youtube.com/@${username}`;

    default:
      return trimmedValue;
  }
};

const BusinessInfoFieldGroup = ({
  intl,
  readOnly,
  forDetailView,
  fullWidth,
}) => {
  const colContainerClassName = fullWidth ? 'col-md-12' : 'col-md-7';

  if (forDetailView) {
    return (
      <Block>
        <div className="row justify-content-center">
          <div className={colContainerClassName}>
            <h5>
              <FormattedMessage {...messages.blockHeading} />
            </h5>

            <Field
              name="name"
              label={intl.formatMessage(messages.businessNameLabelText)}
              validate={composeSyncValidators(required, maxLength())}
              readOnly={readOnly}
              component={FormField}
            />

            <Field
              name="contact_name"
              label={intl.formatMessage(messages.contactNameLabelText)}
              validate={composeSyncValidators(required, maxLength())}
              readOnly={readOnly}
              component={FormField}
            />

            <Field
              name="phone"
              label={intl.formatMessage(messages.phoneLabelText)}
              type="tel"
              validate={composeSyncValidators(required, validPhone())}
              readOnly={readOnly}
              parse={phoneFieldMask}
              component={FormField}
            />

            <Field
              name="business_web_url"
              label={intl.formatMessage(messages.websiteLabelText)}
              type="url"
              readOnly={readOnly}
              component={FormField}
            />

            <Field
              name="about_us"
              label={intl.formatMessage(messages.aboutUsLabelText)}
              readOnly={readOnly}
              component={FormField}
              isTextArea
            />

            <Field
              name="business_description"
              label={intl.formatMessage(
                messages.businessDescriptionLabelText,
              )}
              readOnly={readOnly}
              component={FormField}
              isTextArea
            />

            <hr className="my-4" />

            <h6 className="mb-3">
              <FormattedMessage {...messages.socialsHeadingText} />
            </h6>

            {SOCIAL_FIELDS.map(social => (
              <Field
                key={social.key}
                name={`socials.${social.key}`}
                subscription={{ value: true }}
              >
                {({ input }) => {
                  if (readOnly) {
                    return (
                      <div className="mb-3">
                        <div
                          className="text-muted"
                          style={{ fontSize: '13px' }}
                        >
                          {social.label}
                        </div>

                        {input.value ? (
                          <a
                            href={getSocialUrl(social.key, input.value)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {input.value}
                          </a>
                        ) : (
                          <div>—</div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <FormField
                      input={input}
                      meta={{}}
                      label={social.label}
                      placeholder={social.placeholder}
                    />
                  );
                }}
              </Field>
            ))}

            <hr className="my-4" />

            <h6 className="mb-3">
              <FormattedMessage {...messages.hoursHeadingText} />
            </h6>

            {DAYS.map(day => (
              <div key={day} className="row align-items-center">
                <div className="col-md-4">
                  <strong
                    style={{
                      textTransform: 'capitalize',
                      fontSize: '14px',
                    }}
                  >
                    {day}
                  </strong>
                </div>

                <div className="col-md-4">
                  <Field
                    name={`hours.${day}.open`}
                    label="Open"
                    type="time"
                    readOnly={readOnly}
                    component={FormField}
                  />
                </div>

                <div className="col-md-4">
                  <Field
                    name={`hours.${day}.close`}
                    label="Close"
                    type="time"
                    readOnly={readOnly}
                    component={FormField}
                  />
                </div>
              </div>
            ))}

            <Field
              name="terms_and_conditions"
              label={intl.formatMessage(messages.tAndCLabelText)}
              validate={composeSyncValidators(required, maxLength(300))}
              readOnly={readOnly}
              component={FormField}
              isTextArea
            />
          </div>
        </div>
      </Block>
    );
  }

  return (
    <Block>
      <div className="row justify-content-center">
        <div className={colContainerClassName}>
          <h5>
            <FormattedMessage {...messages.blockHeading} />
          </h5>

          <Field
            name="name"
            label={intl.formatMessage(messages.businessNameLabelText)}
            validate={composeSyncValidators(required, maxLength())}
            readOnly={readOnly}
            component={FormField}
          />

          <Field
            name="contact_name"
            label={intl.formatMessage(messages.contactNameLabelText)}
            validate={composeSyncValidators(required, maxLength())}
            readOnly={readOnly}
            component={FormField}
          />

          <Field
            name="phone"
            label={intl.formatMessage(messages.phoneLabelText)}
            type="tel"
            validate={composeSyncValidators(required, validPhone())}
            readOnly={readOnly}
            parse={phoneFieldMask}
            component={FormField}
          />

          <Field
            name="email"
            label={intl.formatMessage(messages.emailLabelText)}
            type="email"
            autoComplete="off"
            validate={composeSyncValidators(required, validEmail())}
            readOnly={readOnly}
            component={FormField}
          />

          <Field
            name="password"
            label={intl.formatMessage(messages.passwordLabelText)}
            type="password"
            autoComplete="new-password"
            validate={composeSyncValidators(required, validPassword())}
            readOnly={readOnly}
            component={FormField}
          />
        </div>
      </div>
    </Block>
  );
};

BusinessInfoFieldGroup.propTypes = {
  intl: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  forDetailView: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

export default injectIntl(BusinessInfoFieldGroup);